export interface EnvConfig {
  apiUrl: string;
  env: string;
  sentryDsn: string;
  googleClientId?: string;
  defaultAge: number;
  defaultIncome: number;
  recaptchaSiteKey: string;
  serverUrl: string;
  debugHubs: string;
  entraRedirect: string;
  authAuthority: string;
  authHmbPolicyId: string;
  authDomain: string;
  authHmbAppId: string;
  authHmbScope: string;
  authUserPolicyId: string;
  authUserAppId: string;
  authUserScope: string;
  authSignupPolicyId: string;
  authPasswordResetPolicyId: string;
  apiRoot: string;
  /** Short git commit of the running API process (from server build-info.json). */
  serverCommit?: string;
  /** Human-readable build timestamp of the running API process. */
  serverBuildTime?: string;
}

// Create a function to generate the env config with defaults, allowing overrides for testing
export function createEnvConfig(overrides: Partial<EnvConfig> = {}): EnvConfig {
  return {
    apiUrl: import.meta.env.VITE_API_URL || "http://localhost:5016",
    env: import.meta.env.VITE_ENV || "development",
    sentryDsn: import.meta.env.VITE_SENTRY_DSN || "",
    googleClientId: import.meta.env.VITE_GOOGLE_CLIENT_ID || undefined,
    defaultAge: +import.meta.env.VITE_DEFAULT_AGE || 0,
    defaultIncome: +import.meta.env.VITE_DEFAULT_INCOME || 0,
    recaptchaSiteKey: import.meta.env.VITE_RECAPTCHA_SITE_KEY || "",
    serverUrl: import.meta.env.VITE_SERVER_URL || "",
    debugHubs: import.meta.env.VITE_DEBUG_HUBS || "",
    entraRedirect: import.meta.env.VITE_ENTRA_REDIRECT || "",
    authAuthority: import.meta.env.VITE_AUTH_AUTHORITY || "",
    authHmbPolicyId: import.meta.env.VITE_AUTH_HMB_POLICY_ID || "",
    authDomain: import.meta.env.VITE_AUTH_DOMAIN || "",
    authHmbAppId: import.meta.env.VITE_AUTH_HMB_APP_ID || "",
    authHmbScope: import.meta.env.VITE_AUTH_HMB_SCOPE || "",
    authUserPolicyId: import.meta.env.VITE_AUTH_USER_POLICY_ID || "",
    authUserAppId: import.meta.env.VITE_AUTH_USER_APP_ID || "",
    authUserScope: import.meta.env.VITE_AUTH_USER_SCOPE || "",
    authSignupPolicyId: import.meta.env.VITE_AUTH_SIGNUP_POLICY_ID || "",
    authPasswordResetPolicyId:
      import.meta.env.VITE_AUTH_PASSWORD_RESET_POLICY_ID || "",
    apiRoot: import.meta.env.VITE_API_ROOT || "",
    ...overrides,
  };
}

declare global {
  interface Window {
    __APP_CONFIG__: EnvConfig;
  }
}

export async function loadEnvConfig(): Promise<EnvConfig> {
  // VITE_SERVER_URL is the only setting that is needed the .env.development file.
  const url = (import.meta.env.VITE_SERVER_URL ?? ".") + "/envConfig";

  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(
      `Failed to load runtime config from ${url}: ${response.status} ${response.statusText}`,
    );
  }

  const runtimeConfig = (await response.json()) as Partial<EnvConfig>;
  return createEnvConfig(runtimeConfig);
}

export function setEnvConfig(config: EnvConfig): void {
  window.__APP_CONFIG__ = config;
}

export function getEnvConfig(): EnvConfig {
  if (!window.__APP_CONFIG__) {
    throw new Error("App config has not been loaded yet.");
  }
  return window.__APP_CONFIG__;
}
